import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Copy, MapPin, Clock, Download } from "lucide-react";
import { toast } from "sonner";
import { api, imageUrl, formatEUR, getBrowserTz } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import StatusTimeline from "../components/StatusTimeline";
import WhatsAppContextualLink from "../components/WhatsAppContextualLink";
import WhatsAppActionButton from "../components/WhatsAppActionButton";

const ORDER_STEPS = [
  { code: "pendiente", label: "Pendiente" },
  { code: "preparando", label: "Preparando" },
  { code: "enviado", label: "Enviado" },
  { code: "entregado", label: "Entregado" },
];

// legacy → new mapping
const NORMALIZE_ORDER_STATUS = {
  pending: "pendiente",
  confirmed: "pendiente",
  delivered: "entregado",
  cancelled: "cancelado",
};

export default function OrderConfirmationPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { user } = useAuth();

  const load = useCallback(() => {
    api
      .get(`/orders/${id}`)
      .then((r) => setOrder(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="text-3xl font-black tracking-tight">Pedido no encontrado</h1>
        <p className="mt-3 text-neutral-500">
          Es posible que el enlace no sea válido o que estés en otro dispositivo.
        </p>
        <Link
          to="/"
          className="mt-8 inline-block rounded-full bg-[#00953B] hover:bg-[#007a30] text-white px-6 py-3 font-semibold"
        >
          Volver al inicio
        </Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="h-64 rounded-2xl bg-neutral-100 animate-pulse" />
      </div>
    );
  }

  const shortId = order.id.slice(0, 8);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div
      data-testid="order-confirmation-page"
      className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-10"
    >
      <div className="rounded-3xl bg-gradient-to-br from-[#00953B] to-[#00753a] text-white px-6 sm:px-10 py-10">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-white/15 grid place-items-center">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-white/80">
              Pedido confirmado
            </div>
            <h1 className="text-2xl sm:text-3xl font-black leading-tight">
              ¡Gracias! Tu pedido{" "}
              <span data-testid="order-id-short" className="font-mono">
                #{shortId}
              </span>{" "}
              está confirmado
            </h1>
          </div>
        </div>
        <p className="mt-4 text-white/90 max-w-lg text-sm">
          Enviaremos tu compra a tu familia en{" "}
          <strong data-testid="order-camp">{order.camp}</strong> — Daira{" "}
          <strong>{order.daira}</strong>, Barrio <strong>{order.barrio}</strong>.
        </p>
      </div>

      {/* Status timeline */}
      <div className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4 flex items-center gap-2">
          <span>Estado del pedido</span>
          {order.paid ? (
            <span
              data-testid="paid-pill"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-medium normal-case tracking-normal"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Pagado
            </span>
          ) : null}
        </div>
        <StatusTimeline
          steps={ORDER_STEPS}
          currentCode={NORMALIZE_ORDER_STATUS[order.status] || order.status}
          cancelledMessage="Tu pedido ha sido cancelado. Si crees que se trata de un error, contacta con soporte."
        />
      </div>

      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="text-xs text-neutral-500 flex items-center gap-1">
            <MapPin className="h-3 w-3" /> Entrega
          </div>
          <div className="mt-1 font-semibold">{order.camp}</div>
          <div className="text-sm text-neutral-500 mt-0.5">
            {order.daira} · {order.barrio}
          </div>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="text-xs text-neutral-500 flex items-center gap-1">
            <Clock className="h-3 w-3" /> Franja
          </div>
          <div className="mt-1 font-semibold" data-testid="order-slot">
            {order.delivery_slot}
          </div>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="text-xs text-neutral-500">Total</div>
          <div className="mt-1 font-black text-xl" data-testid="order-total">
            {formatEUR(order.total)}
          </div>
        </div>
      </div>

      <section
        data-testid="order-parties"
        className="mt-6 grid sm:grid-cols-2 gap-4"
      >
        <div
          data-testid="order-sender"
          className="rounded-2xl border border-neutral-200 bg-white p-5"
        >
          <div className="text-xs text-neutral-500 mb-2">Remitente</div>
          {order.sender_name && (
            <div className="font-semibold">{order.sender_name}</div>
          )}
          <div className="text-sm text-neutral-600">
            {user?.email || order.guest_email || "—"}
          </div>
          {order.sender_phone && (
            <div className="text-sm text-neutral-600">{order.sender_phone}</div>
          )}
        </div>
        <div
          data-testid="order-recipient"
          className="rounded-2xl border border-neutral-200 bg-white p-5"
        >
          <div className="text-xs text-neutral-500 mb-2 flex items-center gap-1">
            <MapPin className="h-3 w-3" /> Destinatario
          </div>
          {order.recipient_name && (
            <div className="font-semibold">{order.recipient_name}</div>
          )}
          {order.recipient_phone && (
            <div className="text-sm text-neutral-600">{order.recipient_phone}</div>
          )}
          <div className="text-sm text-neutral-600">
            Familia en {order.camp}
          </div>
          <div className="text-sm text-neutral-600">
            Daira: {order.daira} · Barrio: {order.barrio}
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6">
        <h2 className="text-lg font-black tracking-tight mb-4">Productos</h2>
        <div className="space-y-3">
          {order.items.map((it) => (
            <div
              key={it.product_id}
              data-testid={`order-line-${it.product_id}`}
              className="flex gap-3 items-center"
            >
              <div className="h-12 w-12 rounded-lg bg-neutral-50 border border-neutral-100 shrink-0 overflow-hidden">
                {it.image_url && (
                  <img
                    src={imageUrl(it.image_url)}
                    alt={it.name}
                    className="h-full w-full object-contain"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0 text-sm">
                <div className="text-xs text-neutral-500">{it.brand}</div>
                <div className="line-clamp-2 leading-tight">{it.name}</div>
                <div className="text-xs text-neutral-500">
                  x{it.quantity} · {formatEUR(it.unit_price)}
                </div>
              </div>
              <div className="text-sm font-semibold">{formatEUR(it.line_total)}</div>
            </div>
          ))}
        </div>
        <Separator className="my-4" />
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-neutral-600">Subtotal</span>
            <span>{formatEUR(order.subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-neutral-600">Envío</span>
            <span>{formatEUR(order.shipping_cost)}</span>
          </div>
          <div className="flex justify-between font-bold text-base pt-1">
            <span>Total</span>
            <span>{formatEUR(order.total)}</span>
          </div>
        </div>
      </section>

      <WhatsAppContextualLink kind="pedido" shortId={shortId} testId="order-whatsapp-link" />

      <div className="mt-6 flex flex-wrap gap-3">
        {user ? (
          <Button
            asChild
            data-testid="order-to-my-orders-btn"
            className="rounded-full bg-[#00953B] hover:bg-[#007a30] text-white"
          >
            <Link to="/mis-pedidos">Ver mis pedidos</Link>
          </Button>
        ) : (
          <div
            data-testid="order-guest-note"
            className="text-sm rounded-2xl bg-amber-50 text-amber-800 border border-amber-100 px-4 py-3"
          >
            Guarda este enlace o{" "}
            <Link to="/registro" className="underline font-semibold">
              crea una cuenta
            </Link>{" "}
            para volver a verlo desde otro dispositivo.
          </div>
        )}
        <Button
          variant="outline"
          onClick={copyLink}
          data-testid="order-copy-link-btn"
          className="rounded-full border-neutral-200 gap-2"
        >
          <Copy className="h-4 w-4" /> Copiar enlace
        </Button>
        <Button
          onClick={async () => {
            try {
              const resp = await api.get(
                `/orders/${order.id}/receipt.pdf?tz=${encodeURIComponent(getBrowserTz())}`,
                { responseType: "blob" },
              );
              const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = `MercaGo-pedido-${order.id.slice(0, 8)}.pdf`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            } catch {
              toast.error("No se pudo descargar el recibo");
            }
          }}
          data-testid="order-download-pdf-btn"
          className="rounded-full bg-[#00953B] hover:bg-[#007a30] text-white gap-2"
        >
          <Download className="h-4 w-4" /> Descargar recibo (PDF)
        </Button>
        <WhatsAppActionButton
          kind="pedido"
          shortId={shortId}
          orderId={order.id}
          testId="order-open-whatsapp-btn"
        />
        <Button
          asChild
          variant="outline"
          className="rounded-full border-neutral-200"
        >
          <Link to="/">Seguir comprando</Link>
        </Button>
      </div>
    </div>
  );
}
